import useApiRequest from "@/lib/hooks/use-request";
import { formatCurrency, formatDateTimeShort } from "@/lib/hooks/utils";
import { SaleDetailsResponseDto, SaleState } from "@/lib/interfaces/sales";
import PageNotFound from "@/page-components/error/page-not-found";
import { useBreadcrumb } from "@/ui-components/ui/breadcrumb/breadcrumb-context";
import { Button } from "@/ui-components/ui/button";
import DataFetchParent from "@/ui-components/ui/data-fetch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/ui-components/ui/select";
import { Table, TableBody, TableCell, TableRow } from "@/ui-components/ui/table";
import {
  AccordionContainer,
  AccordionContainerParent,
} from "@/ui-components/utils/menu-items/accordion-container-manager";
import { PageTitle, PageTitleContainer } from "@/ui-components/utils/menu-items/page-title";
import { Edit2, X } from "lucide-react";
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getStatusColor } from "../sales-data-table";
import SalesEditor from "../sales-editor";
import ProductManager from "./product-manager";
import ProvisionManager from "./provision-manager";
import { getStatusText } from "@/lib/utils/sale-status";

const SalesItem = () => {
  const { id } = useParams();
  return <>{id ? <SalesItemFetcher id={id} /> : <PageNotFound />}</>;
};

const SalesItemFetcher = ({ id }: { id: string }) => {
  const [item, setItem] = useState<SaleDetailsResponseDto>();
  const [isLoading, setIsLoading] = useState(false);
  const { apiRequest } = useApiRequest();
  const [reload, setReload] = useState(false);

  const getItem = useCallback(async () => {
    setIsLoading(true);
    const response = await apiRequest<SaleDetailsResponseDto>(`sales/${id}`, "GET");
    setIsLoading(false);
    if (response.data) {
      setItem(response.data);
    }
  }, [apiRequest, id]);

  useEffect(() => {
    getItem();
  }, [getItem, reload]);

  return (
    <DataFetchParent
      isLoading={isLoading}
      data={item}
      renderElement={(loadedItem: SaleDetailsResponseDto) => <SalesDetails setReload={setReload} item={loadedItem} />}
    />
  );
};

const SalesDetails = ({
  item,
  setReload,
}: {
  item: SaleDetailsResponseDto;
  setReload: Dispatch<SetStateAction<boolean>>;
}) => {
  const { setTitle } = useBreadcrumb();
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    setTitle(item.title);
  }, [item, setTitle]);

  return (
    <div className="h-full flex flex-col">
      <PageTitleContainer>
        <div className="flex flex-row justify-between items-center min-h-10">
          <PageTitle>
            <div className="flex flex-row gap-2 items-center">Sale: {item.title}</div>
          </PageTitle>
          <div className="flex gap-2">
            <StatusUpdater item={item} setReload={setReload} />
            {item.isEditable && (
              <Button onClick={() => setIsEditing(!isEditing)} className="gap-2" variant="secondary">
                <span>{isEditing ? "Abbrechen" : "Bearbeiten"}</span>
                {isEditing ? <X size={16} /> : <Edit2 size={16} />}
              </Button>
            )}
          </div>
        </div>
      </PageTitleContainer>
      {isEditing ? (
        <div className="p-4 w-1/2 items-center mx-auto">
          <SalesEditor
            item={item}
            onSubmitDone={() => {
              setIsEditing(false);
              setReload((prev) => !prev);
            }}
          />
        </div>
      ) : (
        <AccordionContainerParent>
          <AccordionContainer>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell>Titel</TableCell>
                  <TableCell>{item.title}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Eigenlead</TableCell>
                  <TableCell>{item.ownLead ? "Ja" : "Nein"}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>
                    <span
                      className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${getStatusColor(
                        item.state
                      )}`}
                    >
                      {getStatusText(item.state)}
                    </span>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Netto</TableCell>
                  <TableCell>{formatCurrency(item.totalNetPrice)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Brutto</TableCell>
                  <TableCell>{formatCurrency(item.totalGrossPrice)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Erstellt am</TableCell>
                  <TableCell>{formatDateTimeShort(item.createdAt)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Letzte Änderung</TableCell>
                  <TableCell>{formatDateTimeShort(item.updatedAt)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Fertiggestellt am</TableCell>
                  <TableCell>{item.completedAt ? formatDateTimeShort(item.completedAt) : "-"}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Verkauft von</TableCell>
                  <TableCell>{item.belongsToUserFullName}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Deal</TableCell>
                  <TableCell>
                    <Link to={`/deals/${item.dealsPreview._id}`}>
                      <Button variant="link" className="p-0">
                        {item.dealsPreview.title}
                      </Button>
                    </Link>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </AccordionContainer>
          <div className="p-2 w-full overflow-hidden flex flex-col gap-8">
            <ProductManager item={item} setReload={setReload} />
            <ProvisionManager item={item} setReload={setReload} />
          </div>
        </AccordionContainerParent>
      )}
    </div>
  );
};

const StatusUpdater = ({
  item,
  setReload,
}: {
  item: SaleDetailsResponseDto;
  setReload: Dispatch<SetStateAction<boolean>>;
}) => {
  const { apiRequest } = useApiRequest();
  const [newStatus, setNewStatus] = useState<SaleState | undefined>();

  const toggleStatus = async () => {
    if (newStatus) {
      switch (newStatus) {
        case SaleState.Open:
          await apiRequest<SaleDetailsResponseDto>(`sales/${item._id}/open`, "PATCH", {
            toast: { toastText: "Verkaufsposition erfolgreich geöffnet" },
          });
          break;
        case SaleState.Completed:
          await apiRequest<SaleDetailsResponseDto>(`sales/${item._id}/complete`, "PATCH", {
            toast: { toastText: "Verkaufsposition erfolgreich gebucht" },
          });
          break;
        case SaleState.Cancelled:
          await apiRequest<SaleDetailsResponseDto>(`sales/${item._id}/cancel`, "PATCH", {
            toast: { toastText: "Verkaufsposition erfolgreich storniert" },
          });
          break;
      }
      setReload((prev) => !prev);
    }
  };

  return (
    <div className="flex flex-row gap-2 items-center">
      {item.statusCancelledPossible || item.statusCompletedPossible || item.statusOpenPossible ? (
        <>
          <p className="text-sm text-gray-500 min-w-24">Status ändern</p>
          <Select value={newStatus} onValueChange={(value) => setNewStatus(value as SaleState)}>
            <SelectTrigger>
              <SelectValue placeholder="Status ändern" />
            </SelectTrigger>
            <SelectContent>
              {item.statusOpenPossible && <SelectItem value={SaleState.Open}>Offen</SelectItem>}
              {item.statusCompletedPossible && <SelectItem value={SaleState.Completed}>Gebucht</SelectItem>}
              {item.statusCancelledPossible && <SelectItem value={SaleState.Cancelled}>Storniert</SelectItem>}
            </SelectContent>
          </Select>
          <Button onClick={() => toggleStatus()} disabled={!newStatus}>
            Status aktualisieren
          </Button>
        </>
      ) : (
        <p className="text-sm text-gray-500 min-w-36">Status ändern nicht möglich</p>
      )}
    </div>
  );
};

export default SalesItem;
